import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  AddProductThunk,
  UpdateProductThunk,
  GetSingleProductThunk,
  AllProductsCategoryThunk,
  AllProductsSubCategoryThunk,
  GetVarientlistThunk,
  DeleteGalleryProductThunk,
  AddProductGalleryImageThunk,
} from "../../redux/states/products/thunk";
import NonAuthLayout from "../../Layout/NonAuthLayout";
import {
  Upload,
  Save,
  ArrowLeft,
  Loader2,
  Plus,
  X,
  Trash2,
} from "lucide-react";
import ConfirmDeleteModal from "../../Modals/ConfirmDeleteModal";
import RichTextEditor from "../../components/common/RichTextEditor";

const CreateUpdateProducts = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { uuid } = useParams();
  const { singleProduct, category, subcategory, varients, loading } =
    useSelector((state) => state.products);
  const API_URL = process.env.REACT_APP_API_URL;
  const [loadingVariants, setLoadingVariants] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    sku: "",
    category_id: "",
    category_slug: "",
    sub_category_id: "",
    brand_id: "1",
    brand_name: "",
    unit_id: "1",
    unit_type: "pcs",
    short_description: "",
    long_description: "",
    wholesale_price: "",
    mrp: "",
    stock_quantity: "",
    available_stock: "",
    min_order_qty: "1",
    reorder_level: "",
    visibility: "1",
    status: "active",
    image: null,
    gallery_images: [],
    coupon_code: "",
    coupon_val: "",
    json_product_variants: [],
  });
  const [preview, setPreview] = useState(null);
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [existingGallery, setExistingGallery] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(""); // main | gallery
  const [deleteTarget, setDeleteTarget] = useState(null); // image uuid / index

  /* -------------------- Load Data if Editing -------------------- */
  useEffect(() => {
    if (uuid) {
      setIsUpdating(true);
      dispatch(GetSingleProductThunk(uuid));
    }
  }, [uuid, dispatch]);

  // ✅ Load all categories initially
  useEffect(() => {
    dispatch(AllProductsCategoryThunk());
    dispatch(GetVarientlistThunk());
  }, [dispatch]);

  // ✅ Load subcategories dynamically when category changes
  useEffect(() => {
    if (formData.category_slug) {
      dispatch(AllProductsSubCategoryThunk(formData.category_slug));
    }
  }, [dispatch, formData.category_slug]);

  useEffect(() => {
    if (singleProduct && isUpdating) {
      // Parse variants if they exist
      // ✅ Map backend variants to form structure
      const mappedVariants = singleProduct.product_variants_data
        ? singleProduct?.product_variants_data?.map((v) => ({
            product_id: singleProduct.uuid,
            variant_id: v.variant_id,
            variant_name: v.variant_name,
            variant_value: v.variant_value,
            category_id: singleProduct.category_id,
          }))
        : [];

      const updatedFormData = {
        name: singleProduct.name || "",
        sku: singleProduct.sku || "",
        short_description: singleProduct.short_description || "",
        long_description: singleProduct.long_description || "",
        category_id: singleProduct.category_id || "",
        sub_category_id: singleProduct.sub_category_id || "",
        brand_name: singleProduct.brand_name || "",
        wholesale_price: singleProduct.wholesale_price || "",
        mrp: singleProduct.mrp || "",
        stock_quantity: singleProduct.stock_quantity || "",
        available_stock: singleProduct.available_stock || "",
        min_order_qty:
          singleProduct.min_order_qty ||
          singleProduct.minimum_order_quantity ||
          "",
        reorder_level: singleProduct.reorder_level || "",
        packing_type: singleProduct.packing_type || "",
        unit_id: singleProduct.unit_id || "1",
        unit_type: singleProduct.unit_type || "pcs",
        visibility: singleProduct.visibility || "1",
        status: singleProduct.status || "active",
        coupon_code: singleProduct.coupon_code || "",
        coupon_val: singleProduct.coupon_val || "",
        image: null,
        gallery_images: [],
        json_product_variants: mappedVariants,
      };

      setFormData(updatedFormData);

      // Set selected category for subcategory loading
      const selectedCat = category?.find(
        (cat) => cat.id === singleProduct.category_id
      );
      if (selectedCat) {
        setSelectedCategory(selectedCat.slug || selectedCat.id);
      }

      if (singleProduct.image) {
        setPreview(`${API_URL}/${singleProduct.image}`);
      }
      if (singleProduct.product_images_data) {
        setExistingGallery(singleProduct.product_images_data);
      }
    }
  }, [singleProduct, isUpdating, category, API_URL]);

  /* -------------------- Handle Change -------------------- */
  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "gallery_images") {
      const newFiles = Array.from(files);
      setFormData({
        ...formData,
        gallery_images: [...formData.gallery_images, ...newFiles],
      });

      // Create previews for new files
      const newPreviews = newFiles.map((file) => URL.createObjectURL(file));
      setGalleryPreviews([...galleryPreviews, ...newPreviews]);
      return;
    }

    if (name === "image") {
      if (files && files[0]) {
        setFormData({ ...formData, image: files[0] });
        setPreview(URL.createObjectURL(files[0]));
      }
    } else if (name === "category_id") {
      const selectedCat = category?.find((cat) => cat.id === value);
      setFormData({
        ...formData,
        category_id: value,
        sub_category_id: "", // Reset subcategory
        json_product_variants: [], // Reset variants when category changes
      });
      setSelectedCategory(selectedCat?.slug || "");
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  /* -------------------- Handle Variant Changes -------------------- */
  const addVariant = () => {
    setFormData({
      ...formData,
      json_product_variants: [
        ...formData.json_product_variants,
        {
          product_id: uuid || "",
          variant_id: "",
          variant_name: "",
          variant_value: "",
          category_id: formData.category_id || "",
        },
      ],
    });
  };

  const updateVariant = (index, field, value) => {
    const updatedVariants = [...formData.json_product_variants];
    updatedVariants[index][field] = value;

    // If variant_name is selected from dropdown, automatically set variant_id and category_id
    if (field === "variant_name") {
      const selectedVariant = varients.find((v) => v.variant_name === value);
      if (selectedVariant) {
        updatedVariants[index].variant_id = selectedVariant.id;
        updatedVariants[index].category_id = selectedVariant.category_id;
      }
    }

    setFormData({
      ...formData,
      json_product_variants: updatedVariants,
    });
  };

  const removeVariant = (index) => {
    const updatedVariants = formData.json_product_variants.filter(
      (_, i) => i !== index
    );
    setFormData({
      ...formData,
      json_product_variants: updatedVariants,
    });
  };

  /* -------------------- Submit Form -------------------- */
  const handleSubmit = (e) => {
    e.preventDefault();

    const data = new FormData();

    // Append all form fields
    Object.keys(formData).forEach((key) => {
      if (key === "gallery_images") {
        formData.gallery_images.forEach((img) => {
          data.append("gallery_images[]", img);
        });
      } else if (key === "json_product_variants") {
        if (formData.json_product_variants.length > 0) {
          data.append(key, JSON.stringify(formData.json_product_variants));
        }
      } else if (formData[key] !== "" && formData[key] !== null) {
        data.append(key, formData[key]);
      }
    });

    // Append existing gallery images that haven't been removed
    existingGallery.forEach((img) => {
      data.append("existing_gallery_images[]", img);
    });

    if (isUpdating) {
      dispatch(UpdateProductThunk({ uuid, formData: data }));
      navigate("/products");
    } else {
      dispatch(AddProductThunk(data));
      navigate("/products");
    }
  };

  const handleConfirmDelete = () => {
    if (deleteType === "gallery" && deleteTarget) {
      dispatch(DeleteGalleryProductThunk(deleteTarget));

      // UI se image hatao
      setExistingGallery((prev) =>
        prev.filter((img) => img.uuid !== deleteTarget)
      );
    }

    if (deleteType === "main") {
      setPreview(null);
      setFormData({ ...formData, image: null });
    }

    setModalOpen(false);
    setDeleteTarget(null);
  };

  useEffect(() => {
    if (isUpdating && formData?.product_images_data?.length > 0) {
      const galleryFormData = new FormData();
      formData.product_images_data.forEach((img) => {
        galleryFormData.append("gallery_images[]", img);
      });
      galleryFormData.append("product_uuid", uuid);

      dispatch(AddProductGalleryImageThunk(galleryFormData));

      setFormData((prev) => ({
        ...prev,
        product_images_data: [],
      }));
      setGalleryPreviews([]);
    }
  }, [formData.product_images_data]);

  useEffect(() => {
    if (isUpdating && formData.gallery_images.length > 0 && singleProduct?.id) {
      const galleryFormData = new FormData();

      // ✅ EXACT FIELD NAME REQUIRED BY BACKEND
      formData.gallery_images.forEach((file) => {
        galleryFormData.append("image_path[]", file);
      });

      galleryFormData.append("product_id", singleProduct.id);
      galleryFormData.append("status", "active");

      dispatch(AddProductGalleryImageThunk(galleryFormData));

      // reset
      setFormData((prev) => ({
        ...prev,
        gallery_images: [],
      }));
      setGalleryPreviews([]);
    }
  }, [formData.gallery_images, isUpdating, singleProduct?.id, dispatch]);

  const handleQuillChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  return (
    <NonAuthLayout>
      <div className="min-h-screen py-4 px-2 sm:px-4 lg:px-6">
        <div className="max-w-12xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="mb-4 sm:mb-0">
                <h1 className="text-3xl font-bold text-gray-800">
                  {isUpdating ? "Update Product" : "Create New Product"}
                </h1>
                <p className="text-gray-600 mt-2">
                  {isUpdating
                    ? "Update existing product details"
                    : "Add a new product to your inventory"}
                </p>
              </div>
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors px-4 py-2 border border-gray-300 rounded-lg hover:border-blue-600"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Products
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Basic Information Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6 pb-3 border-b border-gray-200">
                Basic Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Product Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Product Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    placeholder="Enter product name"
                  />
                </div>

                {/* SKU */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SKU *
                  </label>
                  <input
                    type="text"
                    name="sku"
                    value={formData.sku}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    placeholder="Enter SKU"
                  />
                </div>

                {/* Brand Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Brand Name
                  </label>
                  <input
                    type="text"
                    name="brand_name"
                    value={formData.brand_name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    placeholder="Enter brand ID"
                  />
                </div>

                {/* Coupon Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Coupon Code
                  </label>
                  <input
                    type="text"
                    name="coupon_code"
                    value={formData.coupon_code}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    placeholder="Enter coupon code"
                  />
                </div>

                {/* Coupon Value */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Coupon Value
                  </label>
                  <input
                    type="number"
                    name="coupon_val"
                    value={formData.coupon_val}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    placeholder="Enter coupon value"
                    step="0.01"
                  />
                </div>

                {/* Short Description */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Short Description
                  </label>
                  <textarea
                    name="short_description"
                    value={formData.short_description}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    placeholder="Enter short description"
                  />
                </div>

                {/* Long Description */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Long Description
                  </label>
                  <RichTextEditor
                    value={formData.long_description}
                    onChange={(value) =>
                      handleQuillChange("long_description", value)
                    }
                  />
                </div>
              </div>
            </div>

            {/* Category Information Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6 pb-3 border-b border-gray-200">
                Category Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
                {/* Category Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category *
                  </label>
                  <select
                    value={formData.category_id}
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      const selectedCategory = category.find(
                        (cat) => cat.id.toString() === selectedId
                      );

                      setFormData({
                        ...formData,
                        category_id: selectedId, // ✅ backend ke liye
                        category_slug: selectedCategory?.slug, // ✅ subcategory fetch ke liye
                        sub_category_id: "", // reset
                      });
                    }}
                    required
                    className="w-full px-4 py-3 border rounded-xl"
                  >
                    <option value="">Select Category</option>
                    {category?.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Sub Category Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sub Category *
                  </label>
                  <select
                    name="sub_category_id"
                    value={formData.sub_category_id}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        sub_category_id: e.target.value,
                      })
                    }
                    required
                    className="w-full px-4 py-3 border rounded-xl"
                  >
                    <option value="">Select Sub Category</option>
                    {subcategory?.map((sub) => (
                      <option key={sub.id} value={sub.id}>
                        {sub.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Pricing Information Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6 pb-3 border-b border-gray-200">
                Pricing Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Wholesale Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sale Price *
                  </label>
                  <input
                    type="number"
                    name="wholesale_price"
                    value={formData.wholesale_price}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                </div>

                {/* MRP */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Total Price *
                  </label>
                  <input
                    type="number"
                    name="mrp"
                    value={formData.mrp}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>
            </div>

            {/* Stock Information Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6 pb-3 border-b border-gray-200">
                Stock Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Stock Quantity */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Stock Quantity *
                  </label>
                  <input
                    type="number"
                    name="stock_quantity"
                    value={formData.stock_quantity}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    placeholder="0"
                    min="0"
                  />
                </div>

                {/* Available Stock */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Available Stock *
                  </label>
                  <input
                    type="number"
                    name="available_stock"
                    value={formData.available_stock}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    placeholder="0"
                    min="0"
                  />
                </div>
              </div>
            </div>

            {/* Product Details Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6 pb-3 border-b border-gray-200">
                Product Details
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Packing Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Packing Type *
                  </label>
                  <select
                    name="unit_type"
                    value={formData.unit_type}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  >
                    <option value="">Select Packing Type</option>
                    <option value="kg">KG</option>
                    <option value="liter">Liter</option>
                    <option value="pack">Pack</option>
                    <option value="box">Box</option>
                    <option value="unit">Unit</option>
                    <option value="pcs">Pices</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Product Variants Card - UPDATED WITH DROPDOWN */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6 pb-3 border-b border-gray-200">
                Product Variants
              </h2>

              <div className="space-y-4">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <p className="text-gray-600">
                      Add different variants of your product (Size, Color, etc.)
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={addVariant}
                    className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-4 h-4" />
                    Add Variant
                  </button>
                </div>

                {/* Show message if category not selected */}
                {!formData.category_id && (
                  <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-xl">
                    <p className="text-gray-500">
                      Please select a category first to add variants
                    </p>
                  </div>
                )}

                {/* Show loading state */}
                {formData.category_id && loadingVariants && (
                  <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-xl">
                    <p className="text-gray-500">Loading variants...</p>
                  </div>
                )}

                {/* Show variants table */}
                {formData &&
                  !loadingVariants &&
                  formData.json_product_variants.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Variant Name
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Variant Value
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {formData?.json_product_variants?.map(
                            (variant, index) => (
                              <tr key={index}>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <select
                                    value={variant.variant_name}
                                    onChange={(e) =>
                                      updateVariant(
                                        index,
                                        "variant_name",
                                        e.target.value
                                      )
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  >
                                    <option value="">Select Variant</option>
                                    {varients.map((v) => (
                                      <option key={v.id} value={v.variant_name}>
                                        {v.variant_name}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <input
                                    type="text"
                                    value={variant.variant_value}
                                    onChange={(e) =>
                                      updateVariant(
                                        index,
                                        "variant_value",
                                        e.target.value
                                      )
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="e.g., XL, Red, 64GB"
                                  />
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <button
                                    type="button"
                                    onClick={() => removeVariant(index)}
                                    className="flex items-center gap-1 text-red-600 hover:text-red-800"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    Remove
                                  </button>
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}

                {/* Show empty state when category is selected but no variants added */}
                {formData.category_id &&
                  !loadingVariants &&
                  varients.length > 0 &&
                  formData.json_product_variants.length === 0 && (
                    <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-xl">
                      <p className="text-gray-500">
                        {varients.length > 0
                          ? `Found ${varients.length} variant(s) for this category. Click "Add Variant" to get started.`
                          : "No variants found for this category."}
                      </p>
                    </div>
                  )}
              </div>
            </div>

            {/* Status & Media Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6 pb-3 border-b border-gray-200">
                Status & Media
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Status Settings */}
                <div className="space-y-6">
                  {/* Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status *
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="deleted">Deleted</option>
                    </select>
                  </div>
                </div>

                {/* Main Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-4">
                    Product Image *
                  </label>
                  <div className="flex flex-col items-center space-y-4">
                    {preview ? (
                      <div className="relative group">
                        <img
                          src={preview}
                          alt="Product preview"
                          className="w-48 h-48 object-cover rounded-2xl border-2 border-gray-300 shadow-sm"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setPreview(null);
                            setFormData({ ...formData, image: null });
                          }}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-48 h-48 border-2 border-dashed border-gray-300 rounded-2xl flex items-center justify-center bg-gray-50">
                        <Upload className="w-12 h-12 text-gray-400" />
                      </div>
                    )}

                    <label className="flex items-center gap-3 px-6 py-3 bg-blue-600 text-white rounded-xl cursor-pointer hover:bg-blue-700 transition-all duration-300 transform hover:scale-105 shadow-lg">
                      <Upload className="w-5 h-5" />
                      {preview ? "Change Image" : "Upload Product Image"}
                      <input
                        type="file"
                        name="image"
                        onChange={handleChange}
                        className="hidden"
                        accept="image/*"
                        required={!isUpdating}
                      />
                    </label>
                    <p className="text-sm text-gray-500 text-center">
                      Recommended: Square image, 500x500px, max 2MB
                    </p>
                  </div>
                </div>

                {/* Gallery Images */}
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-4">
                    Gallery Images
                  </label>

                  {/* Existing Gallery Images */}
                  {existingGallery.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-sm font-medium text-gray-700 mb-3">
                        Existing Gallery Images:
                      </h3>
                      <div className="flex flex-wrap gap-4">
                        {existingGallery.map((img, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={`${API_URL}/${img?.image_path}`}
                              alt={`Gallery ${index}`}
                              className="w-28 h-28 object-cover"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setDeleteType("gallery");
                                setDeleteTarget(img.uuid); // backend image uuid / path
                                setModalOpen(true);
                              }}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* New Gallery Images */}
                  {(galleryPreviews.length > 0 ||
                    formData.gallery_images.length > 0) && (
                    <div className="mb-6">
                      <h3 className="text-sm font-medium text-gray-700 mb-3">
                        New Gallery Images:
                      </h3>
                      <div className="flex flex-wrap gap-4">
                        {galleryPreviews.map((preview, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={preview}
                              alt={`Gallery preview ${index + 1}`}
                              className="w-24 h-24 object-cover rounded-lg border border-gray-300"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setDeleteType("main");
                                setModalOpen(true);
                              }}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <label className="flex items-center gap-3 px-6 py-3 bg-green-600 text-white rounded-xl cursor-pointer hover:bg-green-700 w-fit transition-colors">
                    <Upload className="w-5 h-5" />
                    Upload Gallery Images
                    <input
                      type="file"
                      name="gallery_images"
                      multiple
                      onChange={handleChange}
                      className="hidden"
                      accept="image/*"
                    />
                  </label>
                  <p className="text-sm text-gray-500 mt-2">
                    You can upload multiple images at once
                  </p>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center gap-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-[1.02] shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {isUpdating ? "Updating Product..." : "Creating Product..."}
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    {isUpdating ? "Update Product" : "Create Product"}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
      <ConfirmDeleteModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Image"
        message="Do you really want to delete this image? This action cannot be undone."
      />
    </NonAuthLayout>
  );
};

export default CreateUpdateProducts;
